<template>
  <div v-if="!pageReady" class="page-state">
    <div v-if="pricePending" class="page-loading">
      <div class="loading-spinner" aria-label="loading"></div>
      <p>{{ $t('regionBar.loading') }}</p>
    </div>
    <div v-else class="page-error">
      <i class="fa-solid fa-triangle-exclamation"></i>
      <h2>{{ $t('regionBar.errorTitle') }}</h2>
      <p>{{ $t('regionBar.errorText', { city: cityName }) }}</p>
      <NuxtLink :to="localePath('/region/bar')" class="error-link">{{ $t('regionBar.backToList') }}</NuxtLink>
    </div>
  </div>
  <div v-else class="prices-page">
    <Head>
      <Title>{{ $t('regionBar.metaTitle', { city: cityName }) }}</Title>
      <Meta name="description" :content="$t('regionBar.metaDescription', { city: cityName })" />
      <Meta property="og:title" :content="$t('regionBar.metaTitle', { city: cityName })" />
      <Meta property="og:description" :content="$t('regionBar.metaDescription', { city: cityName })" />
      <Meta v-if="hasImage" property="og:image" :content="imageUrl" />
      <Link rel="canonical" :href="canonicalUrl" />
    </Head>


    <!-- Hero Header -->
    <section class="hero-sarvban">
      <div class="hero-pattern"></div>
      <div class="hero-glow"></div>
      <div class="geist-container">
        <div class="hero-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
          </svg>
          <span>{{ $t('regionBar.lastUpdate', { date: lastUpdate })  }}</span>
        </div>
        <h1 class="hero-headline">
          {{ ["aquatic","hobobat","mush"].includes(currentCity) ? $t('regionBar.heroTitle_no', { city: cityName }) : $t('regionBar.heroTitle', { city: cityName }) }}
          <span class="headline-accent">{{ $t('regionBar.badge') }}</span>
        </h1>
        <p class="hero-sub">{{ $t('regionBar.heroSubtitle', { city: cityName }) }}</p>
        <div class="hero-actions">
          <NuxtLink :to="localePath('/products')" class="btn-amber" target="_blank">
            <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
              <path d="M7 4l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{{ $t('regionBar.hero_cta') }}</span>
          </NuxtLink>
          <NuxtLink to="/" class="btn-ghost" target="_blank">{{ $t('header.home') }}</NuxtLink>
        </div>
      </div>
    </section>
    <!-- City Sidebar + Main Content -->
    <div class="region-layout">
      <CitySidebar :cities="cityOptions" :current="currentCity" />
      <main class="prices-content">
        <div
          class="price-card"
        >
          <div class="card-header">
            <div class="section-heading d-flex align-items-center justify-content-between mb-3">
              <h6 class="mb-0">
                <i class="fa-solid fa-camera text-success me-2"></i>
                {{ $t('regionBar.priceToday', { city: cityName }) }} {{ $t('regionBar.priceTitle') }}
              </h6>
              <span class="update-badge">
                <i class="fa-solid fa-rotate text-success me-1"></i>
                {{ $t('regionBar.updated') }}
              </span>
            </div>
            <p>بر اساس گزارش دریافتی از بازار و میدان جدیدترین نرخنامه {{ cityName }}(نرخنامه) امروز {{todayDate}} به شرح زیر اعلام میگردد.
              توجه داشته باشید که قیمت های ارائه شده مربوط به انواع میوه عرضه شده در میدان بر اساس نرخنامه میوه و تره بار {{ cityName }}(نرخنامه) امروز بوده و قطعا با قیمت میوه در مغازه ها و سطح شهر متفاوت تر می باشد.</p>
              

          </div>
      <!-- Table View (for cities with table data) -->
      <div v-if="products.length > 0" class="table-section">
        <div
          class="price-card"
        >
          <div class="card-header">
            <div class="section-heading d-flex align-items-center justify-content-between mb-3">
              <h6 class="mb-0">
                <i class="fa-solid fa-table text-success me-2"></i>
                {{ ["aquatic","hobobat","mush"].includes(currentCity) ?  $t('regionBar.priceTodayTitle_no', { city: cityName }) : $t('regionBar.priceTodayTitle', { city: cityName }) }}
              </h6>
              <span class="update-badge">
                <i class="fa-solid fa-rotate text-success me-1"></i>
                {{ $t('regionBar.updated') }}
              </span>
            </div>
          </div>
          <div class="card-body">
            <!-- Search Box -->
            <div class="search-box">
              <i class="fa-solid fa-search text-success"></i>
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="$t('regionBar.searchPlaceholder')"
                class="search-input"
              />
            </div>

            <!-- Table -->
            <div class="table-wrapper">
              <table class="price-table">
                <thead>
                  <tr>
                    <th v-for="header in tableHeaders" :key="header.key">
                      <button 
                        class="th-content"
                        :class="{ sorted: sortKey === header.key }"
                        @click="sortBy(header.key)"
                      >
                        {{ header.label }}
                        <svg v-if="sortKey === header.key" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path :d="sortOrder === 'asc' ? 'm18 15-6-6-6 6' : 'm6 9 6 6 6-6'"/>
                        </svg>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in filteredProducts" :key="index" class="product-row">
                    <td class="name-cell">
                      <div class="product-name">
                        <NuxtLink  v-if="!item.prod_id || item.prod_id == 'null'" :to="localePath(`/search/${item.product_name}`)" class="name-text" :target="'_blank'">
                          {{ item.product_name }}
                        </NuxtLink>                        
                        <NuxtLink v-else :to="localePath(`/cat/${item.prod_id}/${item.product_name}`)" class="name-text" :target="'_blank'">
                          {{ item.product_name }}
                        </NuxtLink>
                        <span v-if="item.type" class="product-type">{{ item.type }}</span>
                      </div>
                    </td>
                    <td v-if="item.maxPrice !== undefined" class="price-cell max">
                      {{ formatPrice(item.maxPrice) }}
                    </td>
                    <td v-if="item.minPrice !== undefined" class="price-cell min">
                      {{ formatPrice(item.minPrice) }}
                    </td>
                    <td v-if="item.price !== undefined" class="price-cell main">
                      {{ formatPrice(item.price) }}
                    </td>
                    <td class="actions-cell">
                      <button class="btn-chart" @click="showChart(item)">
                        <i class="fa-solid fa-chart-line"></i>
                        {{ $t('regionBar.chart') }}
                      </button>
                      <NuxtLink  v-if="item.prod_id" :to="localePath(`/cat/${item.prod_id}/${item.product_name}`)" class="btn-chart" :target="'_blank'">
                        <i class="fa-solid fa-chart-line"></i>
                        {{ $t('regionBar.sellers') }}
                      </NuxtLink>
                      <NuxtLink
                        v-if="item.productId"
                        :to="localePath(`/cat/${item.productId}/${item.name}`)"
                        class="btn-buy"
                      >
                        <i class="fa-solid fa-shopping-cart"></i>
                        {{ $t('regionBar.buy',{product_name:item.product_name}) }}
                      </NuxtLink>
                    </td>
                    <td v-if="item.created_at" class="date-cell">{{ item.created_at }}</td>

                  </tr>
                  <tr v-if="!filteredProducts.length">
                    <td colspan="5" class="empty-state">
                      <i class="fa-solid fa-search"></i>
                      <span>{{ $t('regionBar.notFound') }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

        <div class="info-card">
          <div class="info-content">
            <p>{{ $t('regionBar.info1', { city: cityName }) }}</p>
            <p>{{ $t('regionBar.info2') }}</p>
          </div>
          <NuxtLink to="/" class="info-link">
            {{ $t('regionBar.viewPrices') }}
            <i class="fa fa-solid fa-chevron-left"></i>
          </NuxtLink>
        </div>
            <div v-if="hasImage" class="image-section">
      <!-- Image View (for cities with image data) -->

          <div class="card-body">
            <div
              class="image-wrapper"
              @click="openLightbox"
            >
              <img 
                :src="imageUrl" 
                :alt="$t('regionBar.imageAlt', { city: cityName })" 
                class="price-image" 
                @error="handleImageError"
                @load="handleImageLoad"
              />
              <div class="image-overlay">
                <i class="fa-solid fa-search-plus"></i>
                <span>{{ $t('regionBar.zoomHint') }}</span>
              </div>
            </div>
          </div>
        </div>

      </div>

    </main>
    </div>
    <section class="cta-banner">
      <div class="cta-inner">
        <div class="cta-text">
          <p>{{ $t('regionBar.info3') }}<br/>{{ $t('regionBar.info4') }}<br/>{{ $t('regionBar.info5') }}</p>
        </div>
        <button class="cta-btn" @click="navigateTo(localePath('/products/service'))">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
            <path d="M9 5a2 2 0 012-2h2a2 2 0 012 2v0a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            <path d="M9 14l2 2 4-4"/>
          </svg>
          <span>{{ $t('regionBar.viewSellers') }}</span>
        </button>
      </div>
    </section>
    <!-- ProductList (Sellers) -->
    <div>
      <ProductList
        :products="sellerProducts"
        :title="'فروشندگان برتر در ' + cityName"
        icon="fa-solid fa-store"
        col-class="col-6"
        view-all-link="/products/sellers"
      />
    </div>
    
    <!-- HowSarvbanWorks -->
    <div>
    <HowSarvbanWorks />
  </div>

  <!-- ServiceList (Services) -->
  <div>
    <ProductList
      :products="serviceList"
      :title="'خدمات دهندگان در ' + cityName"
      icon="fa-solid fa-store"
      col-class="col-6"
      view-all-link="/service"
    />
  </div>
  
    <!-- Chart Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showChartModal" class="modal-overlay" @click.self="closeChart">
          <div class="modal-content">
            <div class="modal-header">
              <h3 class="modal-title">{{ chartTitle }}</h3>
              <button class="modal-close" @click="closeChart">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 6 6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <canvas ref="chartCanvas"></canvas>
            </div>
            <div class="modal-footer">
              <button class="btn-close-modal" @click="closeChart">{{ $t('regionBar.close') }}</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Lightbox -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showLightbox" class="lightbox-overlay" @click="closeLightbox">
          <button class="lightbox-close" @click="closeLightbox">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
          <img :src="imageUrl" :alt="$t('regionBar.imageAlt', { city: cityName })" class="lightbox-image" @click.stop />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { Chart, registerables } from 'chart.js'
import { toJalaliParts, formatJalaliDate } from '~/utils/market-date.mjs'

Chart.register(...registerables)

definePageMeta({ layout: 'default' })

const route = useRoute()
const currentCity = computed(() => route.params.city || 'tehran')
const toast  = useToast()

// Reactive data
const searchQuery = ref('')
const sortKey = ref('')
const sortOrder = ref('asc')
const showChartModal = ref(false)
const showLightbox = ref(false)
const chartTitle = ref('')
const chartCanvas = ref(null)
const { t } = useI18n()
const localePath = useLocalePath()

const sellerProducts = ref([])
const serviceList = ref([])
const todayDate = new Date().toLocaleDateString('fa-IR', { year: 'numeric', month: '2-digit', day: '2-digit' })
let chartInstance = null

// This page is public: no login required.
const CityMap = {
  tehran: 'تهران',
  esfahan: 'اصفهان',
  shiraz: 'شیراز',
  najafabad: 'نجف‌آباد',
  fruit: 'میوه و تره بار',
  shahr: 'تره بار شهرداری تهران',
  dam: 'نهاده های دامی',
  hobobat: 'حبوبات',
  aquatic: 'آبزیان',
  mush: 'قارچ',
  bandar: 'بندرعباس',
  mashhad: 'مشهد',
  kerman: 'کرمان',
  alaweer: 'بازار الویر - امارات',
}
const { data: priceData, pending: pricePending, error: priceError } = await useAsyncData(
  `region-bar-price-${currentCity.value}`,
  () => $fetch('/market/api/region/getprice', { query: { city: currentCity.value } }),
  { watch: [currentCity], default: () => null },
)

const products = computed(() => {
  if (!priceData.value) return []
  let data = []
  if (Array.isArray(priceData.value)) {
    data = priceData.value
  } else if (priceData.value.data && Array.isArray(priceData.value.data)) {
    data = priceData.value.data
  }
  // Map API fields to component expected fields
  return data.map(item => ({
    ...item,
    maxPrice: item.high_price ?? item.maxPrice,
    minPrice: item.low_price ?? item.minPrice,
    price: item.lux_price ?? item.price,
    name: item.product_name || item.name,
    prod_id: item.product_id ?? item.prod_id,
    productId: item.product_id ?? item.productId,
    created_at: item.created_at
  }))
})

const hobobatProducts = computed(() => {
  return products.value.map(item => {
    const jalali = item.created_at ? toJalaliParts(item.created_at) : null
    return {
      product_id: item.product_id || item.prod_id || item.productId,
      product_name: item.product_name || item.name,
      date_jalali: jalali ? formatJalaliDate(jalali.year, jalali.month, jalali.day) : '-',
      date_iso: item.created_at || '-',
      price: item.price,
    }
  })
})

const priceImageData = computed(() => {
  if (priceData.value && priceData.value.image) return priceData.value
  return null
})

const pageReady = computed(() => {
  if (pricePending.value) return false
  if (priceError.value) return false
  const d = priceData.value
  if (!d) return false
  if (Array.isArray(d)) return d.length > 0
  // Object response: ready if has image or data array
  const hasImage = !!(d.image || d.date || d.created_at)
  const hasData = !!(d.data && d.data.length > 0)
  return hasImage || hasData
})

async function fetchSellerProducts(city) {
  if(city.includes(['tehran','esfahan','shiraz','bandar','mashhad','kerman',]))
  {
        sellerProducts.value = [] 
      return
  }
  city = CityMap[city] || city
  try {
    const data = await $fetch('/market/api/market/products', {
      method: 'POST',
      body: { ost_txt: city, cat_id: "77",adv_type: 'seller', limit: 10 },
    })
    sellerProducts.value = data?.products || []
  } catch { 
    toast.add({
      title: 'مشکلی در دریافت اطلاعات فروشندگان پیش امده است',
      description: '',
      color: 'red',
      icon: 'i-heroicons-exclamation-circle',
      timeout: 5000,
    })
    sellerProducts.value = [] 
  }
}
async function fetchServiceList(city) {
  if(city.includes(['tehran','esfahan','shiraz','bandar','mashhad','kerman',]))
  {
        sellerProducts.value = [] 
      return
  }
  city = CityMap[city] || city
  try {
    const data = await $fetch('/market/api/market/products', {
      method: 'POST',
      body: { ost_txt: city,cat_id:"48131",adv_type: 'service', limit: 10 },
    })
    serviceList.value = data?.products || []
  } catch { 
    toast.add({
      title: 'مشکلی در دریافت اطلاعات خدمات پیش امده است',
      description: '',
      color: 'red',
      icon: 'i-heroicons-exclamation-circle',
      timeout: 5000,
    })
    serviceList.value = [] 
  }
}

onMounted(() => {
  fetchSellerProducts(currentCity.value)
  fetchServiceList(currentCity.value)
})

watch(currentCity, (city) => {
  fetchSellerProducts(city)
  fetchServiceList(city)
})

const cities = [
  { slug: 'alaweer', name: () => t('regionBar.cities.14') },
  { slug: 'tehran', name: () => t('regionBar.cities.0') },
  { slug: 'esfahan', name: () => t('regionBar.cities.1') },
  { slug: 'shiraz', name: () => t('regionBar.cities.2') },
  { slug: 'najafabad', name: () => t('regionBar.cities.6') },
  { slug: 'yazd', name: () => t('regionBar.cities.11') },
  { slug: 'fruit', name: () => t('regionBar.cities.7') },
  { slug: 'shahr', name: () => t('regionBar.cities.8') },
  { slug: 'mashhad', name: () => t('regionBar.cities.4') },
  { slug: 'dam', name: () => t('regionBar.cities.9') },
  { slug: 'hobobat', name: () => t('regionBar.cities.12') },
  { slug: 'aquatic', name: () => t('regionBar.cities.13') },
  { slug: 'mush', name: () => t('regionBar.cities.10') },
  { slug: 'bandar', name: () => t('regionBar.cities.3') },
  { slug: 'kerman', name: () => t('regionBar.cities.5') },
]

const cityName = computed(() => {
  const city = cities.find(c => c.slug === currentCity.value)
  return city ? city.name() : currentCity.value
})

// Options for the CitySidebar (filterable city list)
const cityOptions = computed(() => cities.map(c => ({ slug: c.slug, label: c.name() })))

const hasImage = computed(() => {
  const result = !!priceImageData.value
  return result
})

const imageUrl = computed(() => {
  
  if (priceImageData.value?.image) {
    const url = `https://sarvban.com/uploads/region/${currentCity.value}/${priceImageData.value.image}`
    return url
  }
  const fallback = `/uploads/region/${currentCity.value}/1400-07-22.jpg`
  return fallback
})

const lastUpdate = computed(() => {
  if (priceImageData.value?.date) {
    return priceImageData.value.date
  }
  return new Date().toLocaleDateString('fa-IR')
})

const requestUrl = useRequestURL()
const appBase = useRuntimeConfig().app.baseURL || '/'
const canonicalUrl = computed(() => requestUrl.href.split('?')[0].split('#')[0])
const siteUrl = computed(() => requestUrl.origin)

const absoluteImageUrl = computed(() => {
  const img = imageUrl.value
  if (/^https?:\/\//.test(img)) return img
  return `${siteUrl.value}${appBase.replace(/\/$/, '')}${img.startsWith('/') ? img : '/' + img}`
})

const schemaNodes = computed(() => {
  const nodes = []
  const title = t('regionBar.metaTitle', { city: cityName.value })

  nodes.push(defineWebPage({
    name: title,
    description: t('regionBar.metaDescription', { city: cityName.value }),
    url: canonicalUrl.value,
    image: absoluteImageUrl.value,
    dateModified: lastUpdate.value,
  }))

  nodes.push(defineBreadcrumb({
    itemListElement: [
      { name: 'سروبان', item: siteUrl.value + appBase },
      { name: t('regionBar.breadcrumbPrices'), item: `/market/region/bar` },
      { name: cityName.value, item: canonicalUrl.value },
    ],
  }))

  if (hasImage.value) {
    nodes.push(defineImage({
      name: t('regionBar.imageAlt', { city: cityName.value }),
      contentUrl: absoluteImageUrl.value,
      url: canonicalUrl.value,
    }))
  }

  if (products.value.length) {
    nodes.push(defineItemList({
      itemListElement: products.value.map((p, i) => defineListItem({
        position: i + 1,
        name: p.product_name || p.name || '',
        item: p.productId ? `/cat/${p.productId}/${encodeURIComponent(p.name || p.product_name || '')}` : undefined,
      })),
    }))
  }

  return nodes
})

useSchemaOrg(schemaNodes)

const tableHeaders = computed(() => {
  const headers = []
  headers.push({ key: 'name', label: t('regionBar.headers.name') })
  if (products.value[0]?.maxPrice !== undefined) {
    headers.push({ key: 'maxPrice', label: t('regionBar.headers.maxPrice') })
    headers.push({ key: 'minPrice', label: t('regionBar.headers.minPrice') })
  } else if (products.value[0]?.price !== undefined) {
    headers.push({ key: 'price', label: t('regionBar.headers.price') })
  }
  headers.push({ key: 'action', label: t('regionBar.actions') })
  if (products.value[0]?.created_at) headers.push({ key: 'date', label: t('regionBar.headers.date') })

  return headers
})

const filteredProducts = computed(() => {
  let result = [...products.value]
  
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(p => (p.product_name || p.name || '').toLowerCase().includes(query))
  }
  
  if (sortKey.value) {
    result.sort((a, b) => {
      const key = sortKey.value === 'name' ? 'product_name' : sortKey.value
      let valA = a[key]
      let valB = b[key]
      if (typeof valA === 'string') {
        valA = valA.localeCompare(valB, 'fa')
        return sortOrder.value === 'asc' ? valA : -valA
      }
      return sortOrder.value === 'asc' ? valA - valB : valB - valA
    })
  }
  
  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function formatPrice(price) {
  const unitText= currentCity.value.includes('alaweer') ?  t('regionBar.dirham') : t('regionBar.priceUnit')
  return new Intl.NumberFormat('fa-IR').format(price) + ' ' + unitText
}
async function showChart(item) {
  const prodName = item.product_name || item.name || ''
  chartTitle.value = t('regionBar.chartTitle', { name: prodName })
  try {
    const data = await $fetch(`/market/api/region/prodprice`, { query: { city: currentCity.value, prod_id: prodName } })
    if (data && data.date_labels?.length) {
      showChartModal.value = true
      await nextTick()
      createChart(data.date_labels, data.minPrice_data || [], data.maxPrice_data || [])
    }
  } catch {
    // ignore
    toast.add({
      title: 'مشکلی در دریافت اطلاعات پیش امده است',
      description: '',
      color: 'red',
      icon: 'i-heroicons-exclamation-circle',
      timeout: 5000,
    })
  }
}

function createChart(labels,minData,maxData) {
  if (chartInstance) {
    chartInstance.destroy()
  }
  
  const ctx = chartCanvas.value?.getContext('2d')
  if (!ctx) return
  
  // // Sample chart data
  // const labels = ['۱۴۰۳/۰۸/۰۱', '۱۴۰۳/۰۸/۰۵', '۱۴۰۳/۰۸/۱۰', '۱۴۰۳/۰۸/۱۵']
  // const minData = [35000, 38000, 36000, 38000]
  // const maxData = [42000, 45000, 43000, 45000]
  
  chartInstance = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: t('regionBar.minPriceLabel'),
          data: minData,
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: t('regionBar.maxPriceLabel'),
          data: maxData,
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            font: { family: 'Vazirmatn, Tahoma, sans-serif' }
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { family: 'Vazirmatn, Tahoma, sans-serif' }
          }
        },
        y: {
          grid: { color: 'rgba(0,0,0,0.05)' },
          ticks: {
            font: { family: 'Vazirmatn, Tahoma, sans-serif' },
            callback: (value) => new Intl.NumberFormat('fa-IR').format(value)
          }
        }
      }
    }
  })
}

function closeChart() {
  showChartModal.value = false
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
}

function openLightbox() {
  showLightbox.value = true
}

function closeLightbox() {
  showLightbox.value = false
}

function handleImageError(e) {
  console.error('Image failed to load:', imageUrl.value)
  const target = e.target
  if (target) {
    target.src = `${siteUrl.value}/uploads/region/${currentCity.value}/1400-07-22.jpg`
  }
}

function handleImageLoad() {
  // console.log('Image loaded successfully:', imageUrl.value)
}
</script>

<style lang="scss" scoped>
@use "sass:color";

$green: #03a054;
$green-dark: #028a46;
$green-light: #e8f5e9;
$saffron: #e8a838;
$saffron-light: #f2d5a0;

.prices-page {
  min-height: 100vh;
  background: #f8fafc;
  direction: rtl;
  margin-top: 80px;
}

/* Loading / Error state */
.page-state {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  background: #f8fafc;
  padding: 48px 16px;
  text-align: center;
  direction: rtl;

  .page-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: #64748b;
    font-size: 0.95rem;

    .loading-spinner {
      width: 42px;
      height: 42px;
      border: 3px solid #e2e8f0;
      border-top-color: #03a054;
      border-radius: 50%;
      animation: page-spin 0.8s linear infinite;
    }
  }

  .page-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: #475569;

    i {
      font-size: 2.4rem;
      color: #f59e0b;
    }

    h2 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 700;
    }

    p {
      margin: 0;
      font-size: 0.9rem;
      color: #64748b;
    }

    .error-link {
      margin-top: 8px;
      padding: 10px 24px;
      background: #03a054;
      color: #fff;
      border-radius: 10px;
      text-decoration: none;
      font-size: 0.9rem;
      font-weight: 600;
    }
  }
}

@keyframes page-spin {
  to { transform: rotate(360deg); }
}

/* Region Layout: sidebar + content */
.region-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 16px;
  align-items: start;
  direction: rtl;

  .prices-content {
    max-width: none;
    margin: 0;
    padding: 0;
    min-width: 0;
  }
}

/* Hero Section */
.hero-sarvban {
  position: relative;
  background: linear-gradient(165deg, #0d2b1d 0%, #1a4a30 50%, #0d2b1d 100%);
  padding: 100px 80px;
  overflow: hidden;
  direction: rtl;
}

.hero-pattern {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 20% 30%, rgba(232, 168, 56, 0.06) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(3, 160, 84, 0.08) 0%, transparent 50%);
  pointer-events: none;
}

.hero-glow {
  position: absolute;
  top: -120px;
  right: -120px;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(232, 168, 56, 0.08) 0%, transparent 60%);
  pointer-events: none;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 100px;
  color: $saffron-light;
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 20px;
  backdrop-filter: blur(4px);
}

.hero-headline {
  font-size: 2.5rem;
  font-weight: 800;
  color: #f0f5ec;
  margin: 0 0 16px;
  line-height: 1.25;
}

.headline-accent {
  color: $saffron;
}

.hero-sub {
  font-size: 1rem;
  color: #b8d0b8;
  margin: 0 0 32px;
  max-width: 560px;
  line-height: 1.7;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.btn-amber {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  background: linear-gradient(135deg, $saffron, $saffron-light);
  color: #fff;
  border-radius: 100px;
  font-weight: 700;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 4px 20px rgba(242, 100, 61, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(242, 100, 61, 0.4);
    color: #fff;
  }
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  padding: 12px 24px;
  color: #e0f0e0;
  border-radius: 100px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
  }
}

@media (max-width: 768px) {
  .hero-sarvban {
    padding: 80px 0 60px;
  }
  .hero-headline {
    font-size: 1.75rem;
  }
  .hero-sub {
    font-size: 0.9rem;
  }

  .price-card {
    .card-header {
      padding: 16px 20px;
      
      .card-title {
        font-size: 1rem;
      }
    }
    
    .card-body {
      padding: 16px;
    }
  }
  
  .price-table {
    font-size: 0.8rem;
    
    th, td {
      padding: 10px 12px;
    }
    
    .actions-cell {
      flex-direction: column;
      
      .btn-chart, .btn-buy {
        width: 100%;
        justify-content: center;
      }
    }
  }
  
  .modal-content {
    border-radius: 16px;
    margin: 10px;
  }
  
  .modal-body {
    padding: 16px;
    
    canvas {
      height: 200px !important;
    }
  }
}

/* Main Content */
.prices-content {
  padding: 24px 16px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Price Card */
.price-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 20px;
  direction: rtl;
  
  .card-header {
    padding: 16px 20px;
    border-bottom: 1px solid #f1f5f9;
    background: #f8f9fa;
  }
  
  .section-heading {
    h6 {
      font-weight: 700;
      color: #333;
      font-size: 1rem;
      display: flex;
      align-items: center;
    }
    
    .update-badge {
      font-size: 0.8rem;
      color: #03a054;
      display: flex;
      align-items: center;
    }
  }
  
  .card-body {
    padding: 20px;
  }
}

/* Image Section */
.image-section {
  .image-wrapper {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    cursor: zoom-in;
    background: #f1f5f9;
    
    .price-image {
      width: 100%;
      height: auto;
      display: block;
      transition: transform 0.3s ease;
    }
    
    .image-overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      opacity: 0;
      transition: opacity 0.3s ease;
      color: #fff;
      text-align: center;
      
      i {
        font-size: 1.5rem;
      }
      
      span {
        font-size: 0.9rem;
      }
    }
    
    &:hover {
      .price-image {
        transform: scale(1.02);
      }
      
      .image-overlay {
        opacity: 1;
      }
    }
  }
}

/* Info Card */
.info-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px 24px;
  margin-top: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  
  .info-content {
    color: #64748b;
    font-size: 0.9rem;
    line-height: 1.8;
    
    p {
      margin: 0 0 12px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  
  .info-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 10px 20px;
    background: $green;
    color: #fff;
    border-radius: 10px;
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 600;
    transition: all 0.25s ease;
    
    svg {
      width: 16px;
      height: 16px;
      transition: transform 0.25s ease;
    }
    
    &:hover {
      background: $green-dark;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(3, 160, 84, 0.3);
      
      svg {
        transform: translateX(-4px);
      }
    }
  }
}

/* Search Box */
.search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  margin-bottom: 20px;
  transition: all 0.2s ease;
  
  i {
    color: #03a054;
    font-size: 0.9rem;
  }
  
  .search-input {
    flex: 1;
    border: none;
    background: none;
    font-size: 0.9rem;
    color: #333;
    outline: none;
    font-family: inherit;
    text-align: right;
    
    &::placeholder {
      color: #999;
    }
  }
  
  &:focus-within {
    border-color: #03a054;
    box-shadow: 0 0 0 2px rgba(3, 160, 84, 0.2);
  }
}

/* Table */
.table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.price-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  direction: rtl;
  
  th {
    padding: 12px 16px;
    text-align: right;
    font-weight: 600;
    color: #666;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    white-space: nowrap;
    
    .th-content {
      display: flex;
      align-items: center;
      gap: 6px;
      background: none;
      border: none;
      color: inherit;
      font: inherit;
      cursor: pointer;
      padding: 0;
      
      i {
        font-size: 0.7rem;
      }
      
      &:hover {
        color: #03a054;
      }
      
      &.sorted {
        color: #03a054;
      }
    }
  }
  
  td {
    padding: 12px 16px;
    border-bottom: 1px solid #f1f5f9;
  }
  
  .product-row {
    transition: background 0.2s ease;
    
    &:hover {
      background: #f8f9fa;
    }
  }
  
  .date-cell {
    color: #999;
    font-size: 0.8rem;
    white-space: nowrap;
  }
  
  .name-cell {
    .product-name {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    
    .name-text {
      font-weight: 600;
      color: #333;
    }
    
    .product-type {
      font-size: 0.75rem;
      color: #999;
    }
  }
  
  .price-cell {
    font-weight: 600;
    white-space: nowrap;
    
    &.max {
      color: #dc3545;
    }
    
    &.min {
      color: #28a745;
    }
    
    &.main {
      color: #03a054;
    }
  }
  
  .actions-cell {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  
  .btn-chart, .btn-buy {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.2s ease;
    cursor: pointer;
    border: none;
    
    i {
      font-size: 0.7rem;
    }
  }
  
  .btn-chart {
    background: #e7f3ff;
    color: #007bff;
    
    &:hover {
      background: #d0e7ff;
    }
  }
  
  .btn-buy {
    background: #e8f5e9;
    color: #28a745;
    
    &:hover {
      background: #d4edda;
    }
  }
  
  .empty-state {
    text-align: center;
    padding: 20px;
    color: #999;
    
    i {
      font-size: 1.2rem;
      margin-bottom: 8px;
    }
    
    span {
      font-size: 0.9rem;
    }
  }
}

.cta-banner {
  padding: 0 16px;
  max-width: 1100px;
  margin: 24px auto 20px;
}

.cta-inner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  background: linear-gradient(135deg,#FFEB3B, #FF9800);
  border-radius: $r-lg;
  border: 1px solid rgba(3, 160, 84, 0.15);
  box-shadow: $shadow-warm-sm;
  flex-wrap: wrap;
}

.cta-text {
  flex: 1;
  min-width: 200px;

  p {
    margin: 0;
    font-size: 1.02rem;
    line-height: 1.7;
    color: $ink-soft;

    strong { color: $leaf; font-weight: 700; }
  }
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  background: linear-gradient(135deg, $leaf, $leaf-soft);
  color: #fff;
  border: none;
  border-radius: $r-pill;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: $shadow-leaf;
  transition: all 0.25s;

  svg { flex-shrink: 0; }

  &:hover { background: linear-gradient(135deg, $leaf-deep, color.adjust($leaf-soft, $lightness: -4%)); }
  &:active { transform: scale(0.97); }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  
  .modal-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
  }
  
  .modal-close {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: none;
    background: #f1f5f9;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    svg {
      width: 20px;
      height: 20px;
      color: #64748b;
    }
    
    &:hover {
      background: #e2e8f0;
      transform: rotate(90deg);
    }
  }
}

.modal-body {
  padding: 24px;
  
  canvas {
    width: 100% !important;
    height: 300px !important;
  }
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  
  .btn-close-modal {
    padding: 10px 24px;
    background: #f1f5f9;
    border: none;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
    
    &:hover {
      background: #e2e8f0;
    }
  }
}

/* Lightbox */
.lightbox-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 40px;
  
  .lightbox-close {
    position: absolute;
    top: 20px;
    left: 20px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    svg {
      width: 24px;
      height: 24px;
      color: #fff;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: rotate(90deg);
    }
  }
  
  .lightbox-image {
    max-width: 100%;
    max-height: 100%;
    border-radius: 8px;
    box-shadow: 0 0 100px rgba(0, 0, 0, 0.5);
  }
}

/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-enter-active .lightbox-image,
.modal-leave-active .modal-content,
.modal-leave-active .lightbox-image {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-content,
.modal-enter-from .lightbox-image {
  transform: scale(0.9) translateY(20px);
}

.modal-leave-to .modal-content,
.modal-leave-to .lightbox-image {
  transform: scale(0.9) translateY(20px);
}

/* Responsive */
@media (max-width: 768px) {
  .hero-sarvban {
    padding: 80px 0 60px;
  }
  
  .hero-headline {
    font-size: 1.75rem;
  }
  
  .hero-sub {
    font-size: 0.9rem;
  }

  .price-card {
    .card-header {
      padding: 16px 20px;
      
      .card-title {
        font-size: 1rem;
      }
    }
    
    .card-body {
      padding: 16px;
    }
  }
  
  .price-table {
    font-size: 0.8rem;
    
    th, td {
      padding: 10px 12px;
    }
    
    .actions-cell {
      flex-direction: column;
      
      .btn-chart, .btn-buy {
        width: 100%;
        justify-content: center;
      }
    }
  }
  
  .modal-content {
    border-radius: 16px;
    margin: 10px;
  }
  
  .modal-body {
    padding: 16px;
    
    canvas {
      height: 200px !important;
    }
  }
}

@media (max-width: 480px) {
  .hero-sarvban {
    padding: 60px 0 40px;
  }
  
  .hero-badge {
    font-size: 0.75rem;
    padding: 4px 12px;
  }
  
  .hero-headline {
    font-size: 1.5rem;
  }
  
  .hero-sub {
    font-size: 0.85rem;
  }
  
  .hero-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .btn-amber,
  .btn-ghost {
    width: 100%;
    justify-content: center;
  }
  
  .price-table {
    .btn-chart, .btn-buy {
      padding: 6px 10px;
      font-size: 0.7rem;
      
      svg {
        width: 12px;
        height: 12px;
      }
    }
  }
}

@media (max-width: 768px) {
  .region-layout {
    grid-template-columns: 1fr;
    padding: 16px;
  }

  .city-sidebar {
    position: static;
  }
}
</style>
